import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { parseCsv, toCsv } from "./csv";
import { autoMap, ENTITIES, EntityType, ImportedRecord, validateRows } from "./schemas";
import "./DataImport.css";

const STORAGE_KEY = "sts-data-import";
const STEPS = ["Source", "Map columns", "Review", "Done"];

type Step = 0 | 1 | 2 | 3;
type Store = Record<EntityType, ImportedRecord[]>;

function loadStore(): Store {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            return JSON.parse(raw);
        }
    } catch (e) {
        // Storage unavailable or corrupted - start empty
    }
    return { items: [], orders: [] };
}

const money = (value: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(value);

export function DataImport() {
    const [entityType, setEntityType] = useState<EntityType>("items");
    const [step, setStep] = useState<Step>(0);
    const [fileName, setFileName] = useState("");
    const [pasteText, setPasteText] = useState("");
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<string[][]>([]);
    const [mapping, setMapping] = useState<Record<string, number>>({});
    const [sourceError, setSourceError] = useState("");
    const [dragging, setDragging] = useState(false);
    const [store, setStore] = useState<Store>(loadStore);
    const [result, setResult] = useState({ inserted: 0, updated: 0, skipped: 0 });
    const [viewType, setViewType] = useState<EntityType>("items");
    const fileInput = useRef<HTMLInputElement>(null);

    const entity = ENTITIES[entityType];

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        } catch (e) {
            // Ignore - persistence is a convenience only
        }
    }, [store]);

    const results = useMemo(
        () => (step >= 2 ? validateRows(entity, rows, mapping, store.items) : []),
        [step, entity, rows, mapping, store.items]
    );
    const validCount = results.filter(r => r.errors.length === 0).length;
    const invalidCount = results.length - validCount;
    const missingRequired = entity.fields.filter(f => f.required && mapping[f.key] < 0);

    const resetWizard = () => {
        setStep(0);
        setFileName("");
        setPasteText("");
        setHeaders([]);
        setRows([]);
        setSourceError("");
    };

    const changeEntity = (type: EntityType) => {
        setEntityType(type);
        setViewType(type);
        resetWizard();
    };

    const loadText = (text: string, name: string) => {
        const parsed = parseCsv(text);
        if (parsed.headers.length === 0 || parsed.rows.length === 0) {
            setSourceError("The file needs a header line and at least one data row.");
            return;
        }
        setSourceError("");
        setFileName(name);
        setHeaders(parsed.headers);
        setRows(parsed.rows);
        setMapping(autoMap(entity, parsed.headers));
        setStep(1);
    };

    const readFile = (file: File | undefined) => {
        if (!file) {
            return;
        }
        if (!/\.(csv|txt|tsv)$/i.test(file.name)) {
            setSourceError("Please choose a .csv, .tsv or .txt file.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => loadText(String(reader.result || ""), file.name);
        reader.onerror = () => setSourceError("Could not read the file.");
        reader.readAsText(file);
    };

    const downloadTemplate = () => {
        const csv = toCsv(entity.fields.map(f => f.key), []);
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `${entity.type}-template.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const runImport = () => {
        const valid = results.filter(r => r.errors.length === 0).map(r => r.record);
        const existing = store[entityType].slice();
        let inserted = 0;
        let updated = 0;

        valid.forEach(record => {
            const index = existing.findIndex(e => e[entity.keyField] === record[entity.keyField]);
            if (index >= 0) {
                existing[index] = { ...existing[index], ...record };
                updated++;
            } else {
                existing.push(record);
                inserted++;
            }
        });

        setStore({ ...store, [entityType]: existing });
        setResult({ inserted, updated, skipped: results.length - valid.length });
        setViewType(entityType);
        setStep(3);
    };

    return (
        <div className="imp">
            <div className="imp-top">
                <div className="imp-entity" role="tablist">
                    {(Object.keys(ENTITIES) as EntityType[]).map(type => (
                        <button
                            key={type}
                            role="tab"
                            aria-selected={type === entityType}
                            className={type === entityType ? "active" : ""}
                            onClick={() => changeEntity(type)}
                        >
                            Import {ENTITIES[type].label}
                        </button>
                    ))}
                </div>
                <ol className="imp-steps">
                    {STEPS.map((label, i) => (
                        <li key={label} className={i === step ? "current" : i < step ? "done" : ""}>
                            <span>{i < step ? "✓" : i + 1}</span>
                            {label}
                        </li>
                    ))}
                </ol>
            </div>

            <div className="imp-panel">
                {step === 0 && (
                    <div className="imp-source">
                        <div
                            className={`imp-drop ${dragging ? "dragging" : ""}`}
                            onClick={() => fileInput.current?.click()}
                            onDragOver={e => {
                                e.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={e => {
                                e.preventDefault();
                                setDragging(false);
                                readFile(e.dataTransfer.files[0]);
                            }}
                        >
                            <CloudUploadOutlinedIcon className="imp-drop-icon" />
                            <strong>Drop a CSV file here or click to browse</strong>
                            <small>Comma, semicolon and tab separated files are supported</small>
                            <input
                                ref={fileInput}
                                type="file"
                                accept=".csv,.tsv,.txt"
                                hidden
                                onChange={e => {
                                    readFile(e.target.files ? e.target.files[0] : undefined);
                                    e.target.value = "";
                                }}
                            />
                        </div>

                        <div className="imp-or"><span>or paste data</span></div>

                        <textarea
                            value={pasteText}
                            onChange={e => setPasteText(e.target.value)}
                            placeholder={entity.fields.map(f => f.key).join(",")}
                            rows={5}
                        />

                        {sourceError && <p className="imp-error">{sourceError}</p>}

                        <div className="imp-actions">
                            <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
                                Template
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => loadText(entity.sample, `sample-${entity.type}.csv`)}>
                                Use sample data
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={pasteText.trim() === ""}
                                onClick={() => loadText(pasteText, "pasted data")}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <div>
                        <p className="imp-info">
                            <strong>{fileName}</strong> · {rows.length} rows · {headers.length} columns. We matched the
                            columns automatically - adjust if needed.
                        </p>
                        <div className="imp-mapping">
                            {entity.fields.map(field => {
                                const column = mapping[field.key];
                                return (
                                    <div key={field.key} className={`imp-map-row ${column < 0 && field.required ? "missing" : ""}`}>
                                        <div className="imp-map-target">
                                            {field.label}
                                            {field.required && <span className="imp-required">*</span>}
                                            <small>{field.type}</small>
                                        </div>
                                        <span className="imp-map-arrow">←</span>
                                        <select
                                            value={column}
                                            onChange={e => setMapping({ ...mapping, [field.key]: Number(e.target.value) })}
                                        >
                                            <option value={-1}>- not mapped -</option>
                                            {headers.map((h, i) => (
                                                <option key={i} value={i}>{h || `Column ${i + 1}`}</option>
                                            ))}
                                        </select>
                                        <span className="imp-map-sample">
                                            {column >= 0 && rows[0] ? rows[0][column] : ""}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        {missingRequired.length > 0 && (
                            <p className="imp-error">
                                Map the required fields: {missingRequired.map(f => f.label).join(", ")}
                            </p>
                        )}
                        <div className="imp-actions">
                            <Button size="small" onClick={resetWizard}>Back</Button>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={missingRequired.length > 0}
                                onClick={() => setStep(2)}
                            >
                                Validate
                            </Button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <div className="imp-stats">
                            <div className="ok"><CheckCircleIcon /> <strong>{validCount}</strong> ready</div>
                            <div className="bad"><ErrorOutlineIcon /> <strong>{invalidCount}</strong> with errors (skipped)</div>
                        </div>
                        <div className="imp-table-wrap">
                            <table className="imp-table">
                                <thead>
                                    <tr>
                                        <th>Line</th>
                                        {entity.fields.map(f => <th key={f.key}>{f.label}</th>)}
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map(r => (
                                        <tr key={r.rowNumber} className={r.errors.length ? "row-error" : ""}>
                                            <td className="imp-muted">{r.rowNumber}</td>
                                            {entity.fields.map(f => {
                                                const column = mapping[f.key];
                                                const raw = column >= 0 ? rows[r.rowNumber - 2][column] : "";
                                                return <td key={f.key}>{raw}</td>;
                                            })}
                                            <td className="imp-status">
                                                {r.errors.map(e => (
                                                    <span key={e} className="imp-badge bad">{e}</span>
                                                ))}
                                                {r.warnings.map(w => (
                                                    <span key={w} className="imp-badge warn"><WarningAmberIcon fontSize="inherit" /> {w}</span>
                                                ))}
                                                {r.errors.length === 0 && r.warnings.length === 0 && (
                                                    <span className="imp-badge ok">OK</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="imp-actions">
                            <Button size="small" onClick={() => setStep(1)}>Back</Button>
                            <Button size="small" variant="contained" disabled={validCount === 0} onClick={runImport}>
                                Import {validCount} {entity.label.toLowerCase()}
                            </Button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="imp-done">
                        <CheckCircleIcon className="imp-done-icon" />
                        <h3>Import finished</h3>
                        <p>
                            <strong>{result.inserted}</strong> created · <strong>{result.updated}</strong> updated ·{" "}
                            <strong>{result.skipped}</strong> skipped
                        </p>
                        <div className="imp-actions center">
                            <Button size="small" variant="outlined" onClick={resetWizard}>Import more</Button>
                            {entityType === "items" && (
                                <Button size="small" variant="contained" onClick={() => changeEntity("orders")}>
                                    Next: import orders
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <DataViewer store={store} viewType={viewType} onViewChange={setViewType} onClear={type => setStore({ ...store, [type]: [] })} />
        </div>
    );
}

interface DataViewerProps {
    store: Store;
    viewType: EntityType;
    onViewChange: (type: EntityType) => void;
    onClear: (type: EntityType) => void;
}

function DataViewer({ store, viewType, onViewChange, onClear }: DataViewerProps) {
    const entity = ENTITIES[viewType];
    const records = store[viewType];
    const priceBySku: Record<string, number> = {};
    store.items.forEach(item => {
        priceBySku[String(item.sku)] = Number(item.price) || 0;
    });

    return (
        <div className="imp-data">
            <div className="imp-data-head">
                <h3>Imported data</h3>
                <div className="imp-data-tabs">
                    {(Object.keys(ENTITIES) as EntityType[]).map(type => (
                        <button key={type} className={type === viewType ? "active" : ""} onClick={() => onViewChange(type)}>
                            {ENTITIES[type].label} <span>{store[type].length}</span>
                        </button>
                    ))}
                </div>
                {records.length > 0 && (
                    <button className="imp-clear" onClick={() => onClear(viewType)}>Clear {entity.label.toLowerCase()}</button>
                )}
            </div>

            {records.length === 0 ? (
                <p className="imp-muted imp-empty">No {entity.label.toLowerCase()} imported yet.</p>
            ) : (
                <div className="imp-table-wrap">
                    <table className="imp-table">
                        <thead>
                            <tr>
                                {entity.fields.map(f => <th key={f.key}>{f.label}</th>)}
                                {viewType === "orders" && <th>Total</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {records.map(record => (
                                <tr key={String(record[entity.keyField])}>
                                    {entity.fields.map(f => (
                                        <td key={f.key}>
                                            {f.key === "price" && record[f.key] !== undefined
                                                ? money(Number(record[f.key]))
                                                : record[f.key] !== undefined ? String(record[f.key]) : "-"}
                                        </td>
                                    ))}
                                    {viewType === "orders" && (
                                        <td>
                                            {priceBySku[String(record.sku)] !== undefined
                                                ? money(priceBySku[String(record.sku)] * Number(record.quantity))
                                                : <span className="imp-muted">unknown item</span>}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
